import { useState } from "react";
import { toast } from "sonner";
import { Printer, Usb } from "lucide-react";
import { getPrintSettings, setPrintSettings, getPairedPrinter, pairPrinterUsb, kickDrawer } from "@/lib/printer";

function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <label data-testid={testid} className="flex items-start justify-between gap-3 bg-stone-50 rounded-2xl border border-[#E5DECF] p-4 cursor-pointer">
      <span>
        <span className="font-bold text-sm block">{label}</span>
        <span className="text-xs text-stone-500 block mt-0.5">{desc}</span>
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-emerald-700 mt-0.5 shrink-0" />
    </label>
  );
}

export default function PrinterTab() {
  const [s, setS] = useState(getPrintSettings());
  const [paired, setPaired] = useState(getPairedPrinter());
  const [kicking, setKicking] = useState(false);

  const upd = (k, v) => {
    const ns = { ...s, [k]: v };
    setS(ns);
    setPrintSettings(ns);
    toast.success("Pengaturan tersimpan di perangkat ini");
  };

  const pair = async () => {
    try {
      const name = await pairPrinterUsb();
      setPaired(name);
      toast.success(`Printer terhubung: ${name}`);
    } catch (e) { toast.error(e?.message || "Gagal memasangkan printer"); }
  };

  const testKick = async () => {
    setKicking(true);
    const ok = await kickDrawer();
    setKicking(false);
    if (ok) toast.success("Perintah buka laci terkirim");
    else toast.error(paired ? "Gagal mengirim — pastikan printer USB tersambung & tidak dipakai aplikasi lain" : "Belum ada printer USB terpasang — tekan 'Pasangkan Printer USB' dulu");
  };

  return (
    <div className="mt-6 space-y-4 max-w-2xl" data-testid="printer-tab">
      <div className="bg-white rounded-3xl border border-[#E5DECF] p-5">
        <h3 className="font-display font-bold text-xl flex items-center gap-2"><Printer className="w-5 h-5" /> Pengaturan Printer</h3>
        <p className="text-sm text-stone-500 mt-1">Pengaturan ini tersimpan <b>per perangkat</b> — atur terpisah di PC kasir, tablet, maupun HP.</p>
        <div className="mt-4 space-y-3">
          <Toggle testid="printer-autoprint-toggle" label="Cetak struk otomatis setelah pembayaran" desc="Begitu transaksi tersimpan, struk langsung dikirim ke printer tanpa perlu menekan tombol Cetak Struk." checked={s.autoprint} onChange={(v) => upd("autoprint", v)} />
          <Toggle testid="printer-drawer-toggle" label="Buka laci kasir saat pembayaran tunai" desc="Mengirim perintah buka laci ke printer thermal via USB — termasuk saat memakai tombol Simpan (Tanpa Cetak)." checked={s.drawer_kick} onChange={(v) => upd("drawer_kick", v)} />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E5DECF] p-5">
        <h4 className="font-display font-bold text-lg flex items-center gap-2"><Usb className="w-5 h-5" /> Laci Kasir (USB)</h4>
        <p className="text-xs text-stone-500 mt-1">Laci kasir tersambung ke printer thermal (kabel RJ11). Pasangkan printer via USB agar aplikasi bisa membuka laci tanpa mencetak.</p>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <button data-testid="printer-pair-btn" onClick={pair} className="px-4 py-2.5 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-emerald-700 transition">Pasangkan Printer USB</button>
          <button data-testid="printer-test-kick-btn" onClick={testKick} disabled={kicking} className="px-4 py-2.5 rounded-full bg-amber-100 text-amber-800 text-sm font-bold border border-amber-300 hover:bg-amber-200 transition disabled:opacity-50">{kicking ? "Mengirim…" : "Tes Buka Laci"}</button>
        </div>
        <p data-testid="printer-paired-status" className="text-xs mt-2 font-semibold {paired ? 'text-emerald-700' : 'text-stone-400'}">{paired ? `✓ Terpasang: ${paired}` : "Belum ada printer terpasang"}</p>
      </div>

      <div className="bg-white rounded-3xl border border-[#E5DECF] p-5">
        <h4 className="font-display font-bold text-lg">Cetak Langsung Tanpa Preview (Silent Print)</h4>
        <div className="text-xs text-stone-600 mt-2 space-y-3">
          <div>
            <p className="font-bold text-stone-800">🖥 PC / Laptop kasir (Windows) — printer POS80:</p>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Jadikan <b>POS80 Printer</b> sebagai printer default Windows (Settings → Printers).</li>
              <li>Klik kanan shortcut Chrome → <b>Properties</b> → di kolom <b>Target</b>, tambahkan <code className="bg-stone-100 px-1 rounded"> --kiosk-printing</code> di paling belakang.</li>
              <li>Selalu buka kasir lewat shortcut itu → cetak struk langsung keluar, <b>tanpa jendela preview</b>.</li>
              <li>Agar laci terbuka tiap cetak: di driver POS80 aktifkan <i>"Open cash drawer at end of document"</i>.</li>
            </ol>
          </div>
          <div>
            <p className="font-bold text-stone-800">📱 Tablet / HP Android:</p>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Pasang aplikasi <b>RawBT</b> (gratis, Play Store) dan hubungkan printer thermal.</li>
              <li>Saat dialog cetak muncul, pilih <b>RawBT</b> sebagai tujuan — RawBT mencetak format struk thermal dengan rapi dan bisa buka laci + potong kertas otomatis.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
